import React, { useState, useEffect } from 'react';
import { Routes, Route, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { THEMES } from './data/theme';
import { NAV_ITEMS } from './data/mockData';
import Sidebar from './components/layout/Sidebar';
import Topbar from './components/layout/Topbar';
import { ToastContainer, PlaceholderPage } from './components/shared';
import Dashboard    from './pages/Dashboard';
import Devices      from './pages/Devices';
import DeviceDetail from './pages/DeviceDetail';
import Policies     from './pages/Policies';
import Apps         from './pages/Apps';
import Users        from './pages/Users';
import Reports      from './pages/Reports';
import Settings     from './pages/Settings';
import AdminPanel       from './pages/AdminPanel';
import QREnrollment     from './pages/QREnrollment';
import MDMScanner       from './pages/MDMScanner';
import MdmAppsManagement from './pages/MdmAppsManagement';
import Login            from './pages/Login';
import Register         from './pages/Register';

function RequireAuth({ children, auth }) {
  const location = useLocation();
  if (!auth?.token) return <Navigate to='/login' state={{ from: location }} replace />;
  return children;
}

function AppLayout({ theme, setTheme, C, auth, setAuth }) {
  const location  = useLocation();
  const navigate  = useNavigate();
  const [collapsed, setCollapsed]   = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isMobile, setIsMobile]     = useState(false);
  const [time, setTime]             = useState(new Date());

  useEffect(() => { const t = setInterval(() => setTime(new Date()), 1000); return () => clearInterval(t); }, []);
  useEffect(() => {
    const check = () => { const m = window.innerWidth < 768; setIsMobile(m); if (m) setCollapsed(false); };
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const pathKey = (() => {
    const seg = location.pathname.split('/')[1];
    if (!seg) return 'dashboard';
    if (seg === 'admin') return 'admin';
    return NAV_ITEMS.find(n => n.key === seg)?.key || 'dashboard';
  })();

  const pageLabels = {
    dashboard:'Genel Bakış', devices:'Cihazlar', policies:'Politikalar',
    apps:'Uygulamalar', users:'Kullanıcılar', reports:'Raporlar',
    settings:'Ayarlar', admin:'Admin Paneli', enrollment:'MDM Kaydı',
  };
  const pageLabel = pageLabels[pathKey] || '';

  const doNav = (key) => {
    if (key === 'admin') return navigate('/admin');
    navigate(key === 'dashboard' ? '/' : `/${key}`);
  };

  const isAdmin = auth?.user?.is_system_owner || auth?.user?.role === 'Süper Admin';

  return (
    <div style={{ display:'flex', height:'100vh', background:C.bg, color:C.text, fontFamily:"'DM Sans','Segoe UI',sans-serif", fontSize:14, overflow:'hidden', transition:'background 0.25s,color 0.25s' }}>
      {!isMobile && (
        <Sidebar theme={theme} C={C} collapsed={collapsed} onToggle={() => setCollapsed(c=>!c)} active={pathKey} onNav={doNav} mobile={false} currentUser={auth?.user} />
      )}
      {isMobile && mobileOpen && (
        <>
          <div onClick={() => setMobileOpen(false)} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', zIndex:40, backdropFilter:'blur(2px)' }} />
          <div style={{ position:'fixed', left:0, top:0, bottom:0, width:240, zIndex:50 }}>
            <Sidebar theme={theme} C={C} collapsed={false} onToggle={()=>{}} active={pathKey} onNav={key => { doNav(key); setMobileOpen(false); }} mobile={true} onClose={() => setMobileOpen(false)} currentUser={auth?.user} />
          </div>
        </>
      )}

      <div style={{ flex:1, display:'flex', flexDirection:'column', overflow:'hidden', minWidth:0 }}>
        <Topbar theme={theme} setTheme={setTheme} onMenuClick={() => setMobileOpen(true)} isMobile={isMobile} time={time} C={C} currentUser={auth?.user} />

        <div style={{ padding: isMobile ? '12px 16px 0' : '10px 24px 0', flexShrink:0 }}>
          {!isMobile && (
            <div style={{ display:'flex', alignItems:'center', gap:5, fontSize:11.5, color:C.textFaint, marginBottom:4 }}>
              <span>Ana Sayfa</span><span>›</span>
              <span style={{ color:C.textSub, fontWeight:500 }}>{pageLabel}</span>
            </div>
          )}
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <div style={{ fontSize:isMobile?20:24, fontWeight:800, color:C.text, letterSpacing:'-0.6px' }}>{pageLabel}</div>
            {pathKey === 'admin' && (
              <span style={{ fontSize:11, fontWeight:700, background:'rgba(245,158,11,0.15)', color:'#f59e0b', border:'1px solid rgba(245,158,11,0.3)', padding:'2px 10px', borderRadius:99 }}>🔐 Yönetici Görünümü</span>
            )}
          </div>
          {!isMobile && <div style={{ fontSize:12, color:C.textFaint, marginTop:2 }}>{new Date().toLocaleDateString('tr-TR',{weekday:'long',day:'numeric',month:'long',year:'numeric'})}</div>}
        </div>

        <div style={{ flex:1, overflowY:'auto', padding:isMobile?'14px 16px':'16px 24px' }}>
          <Routes>
            <Route path='/'            element={<Dashboard   C={C} theme={theme} isMobile={isMobile} />} />
            <Route path='/devices'     element={<Devices     C={C} theme={theme} isMobile={isMobile} />} />
            <Route path='/devices/:id' element={<DeviceDetail C={C} theme={theme} isMobile={isMobile} />} />
            <Route path='/policies'    element={<Policies    C={C} theme={theme} isMobile={isMobile} />} />
            <Route path='/apps'        element={<Apps        C={C} theme={theme} isMobile={isMobile} />} />
            <Route path='/users'       element={<Users       C={C} theme={theme} isMobile={isMobile} />} />
            <Route path='/reports'     element={<Reports     C={C} theme={theme} isMobile={isMobile} />} />
            <Route path='/settings'    element={<Settings    C={C} theme={theme} setTheme={setTheme} isMobile={isMobile} />} />
            <Route path='/admin' element={
              isAdmin ? <AdminPanel C={C} currentUser={auth?.user} /> : <Navigate to='/' replace />
            } />
            <Route path='/enrollment' element={
              isAdmin ? <QREnrollment C={C} currentUser={auth?.user} /> : <Navigate to='/' replace />
            } />
            <Route path='/apps-management' element={
              isAdmin ? <MdmAppsManagement C={C} currentUser={auth?.user} /> : <Navigate to='/' replace />
            } />
            <Route path='/mdm-scanner' element={<MDMScanner C={C} />} />
            <Route path='*' element={<PlaceholderPage C={C} title='Sayfa bulunamadı' />} />
          </Routes>
        </div>
      </div>
    </div>
  );
}

export default function App() {
  const [theme, setTheme] = useState(() => localStorage.getItem('mdm_theme') || 'dark');
  const [auth, setAuth]   = useState(() => {
    try {
      const token = localStorage.getItem('mdm_token');
      const user  = JSON.parse(localStorage.getItem('mdm_user') || 'null');
      return token && user ? { token, user } : null;
    } catch { return null; }
  });

  const C = THEMES[theme];

  useEffect(() => { localStorage.setItem('mdm_theme', theme); }, [theme]);

  const handleSetAuth = (a) => {
    setAuth(a);
    if (a) {
      localStorage.setItem('mdm_token', a.token);
      localStorage.setItem('mdm_user', JSON.stringify(a.user));
    } else {
      localStorage.removeItem('mdm_token');
      localStorage.removeItem('mdm_user');
    }
  };

  return (
    <>
      <Routes>
        <Route path='/login'    element={<Login    setAuth={handleSetAuth} />} />
        <Route path='/register' element={<Register />} />
        <Route path='/*' element={
          <RequireAuth auth={auth}>
            <AppLayout theme={theme} setTheme={setTheme} C={C} auth={auth} setAuth={handleSetAuth} />
          </RequireAuth>
        } />
      </Routes>
      <ToastContainer />
    </>
  );
}
